/**
 * Create pool page — uses Card, RadioGroup, Textarea, Alert primitives.
 */

import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Textarea from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import PageHeader from '../components/ui/page-header';
import { Alert, AlertDescription } from '../components/ui/alert';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { useCreatePool } from '../hooks/usePools';
import { useAppStore } from '../stores/appStore';
import { createPoolSchema, type CreatePoolForm } from './pool-create-schema';

export default function PoolCreatePage() {
  const createPool = useCreatePool();
  const user = useAppStore((s) => s.user);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<CreatePoolForm>({
    resolver: zodResolver(createPoolSchema),
    defaultValues: { isPublic: true },
  });

  const isFree = user?.plan === 'free';

  const onSubmit = (data: CreatePoolForm) => {
    if (isFree) return;
    createPool.mutate(data);
  };

  return (
    <div className="max-w-2xl mx-auto">
      <PageHeader
        back={
          <Link
            to="/boloes"
            className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar para bolões
          </Link>
        }
        title="Criar novo bolão"
        description="Dê um nome ao seu bolão, escolha a visibilidade e convide os amigos."
      />

      {isFree && (
        <Alert variant="warning" className="mb-6">
          <AlertDescription>
            Contas gratuitas podem usar apenas o bolão demo. Faça upgrade para criar
            novos bolões.
          </AlertDescription>
        </Alert>
      )}

      <Card className="p-6 sm:p-8">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          <Input
            {...register('name')}
            label="Nome do bolão"
            placeholder="Ex: Bolão da família"
            error={errors.name?.message}
            disabled={isFree}
          />

          <div className="space-y-2">
            <label
              htmlFor="pool-description"
              className="block text-sm font-medium text-neutral-700 dark:text-neutral-300"
            >
              Descrição (opcional)
            </label>
            <Textarea
              id="pool-description"
              {...register('description')}
              rows={3}
              placeholder="Descreva o bolão para os participantes…"
              disabled={isFree}
              aria-invalid={!!errors.description}
            />
            {errors.description && (
              <p className="text-sm text-destructive">{errors.description.message}</p>
            )}
          </div>

          <fieldset className="space-y-3">
            <legend className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Visibilidade
            </legend>
            <Controller
              control={control}
              name="isPublic"
              render={({ field }) => (
                <RadioGroup
                  value={String(field.value)}
                  onValueChange={(v) => field.onChange(v === 'true')}
                  disabled={isFree}
                  aria-label="Visibilidade do bolão"
                >
                  <label className="flex items-start gap-3 rounded-xl border border-neutral-200 dark:border-neutral-700 p-4 cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-colors data-[disabled]:opacity-50 data-[disabled]:cursor-not-allowed">
                    <RadioGroupItem value="true" className="mt-0.5" />
                    <div className="flex-1">
                      <span className="block font-medium text-foreground">Público</span>
                      <p className="text-sm text-muted-foreground">
                        Qualquer pessoa pode ver e entrar no bolão.
                      </p>
                    </div>
                  </label>
                  <label className="flex items-start gap-3 rounded-xl border border-neutral-200 dark:border-neutral-700 p-4 cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-colors data-[disabled]:opacity-50 data-[disabled]:cursor-not-allowed">
                    <RadioGroupItem value="false" className="mt-0.5" />
                    <div className="flex-1">
                      <span className="block font-medium text-foreground">Privado</span>
                      <p className="text-sm text-muted-foreground">
                        Apenas pessoas com o código de convite podem entrar.
                      </p>
                    </div>
                  </label>
                </RadioGroup>
              )}
            />
          </fieldset>

          <div className="flex gap-3 pt-2">
            <Link to="/boloes" className="flex-1">
              <Button type="button" variant="outline" className="w-full">
                Cancelar
              </Button>
            </Link>
            <Button
              type="submit"
              className="flex-1"
              loading={createPool.isPending}
              disabled={isFree}
            >
              Criar bolão
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
